import React, { FC, ReactElement } from 'react';
import classNames from 'classnames';
import { useApolloClient } from '@apollo/client';

import styles from './positionInformationStyles.scss';

import BasicInformation from './basicInformation';
import Fundamentals from './EquityType/fundamentals';
import IndentureData from './CreditType/indentureData';
import InfoTable from 'buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';
import MultiplesRatios from './EquityType/multiplesRatios';
import PositionInformationLoading from 'buyside/components/presentation/IdeaShowPage/RightSidebar/PositionInformation/positionInformationLoading';
import SecurityData from './CreditType/securityData';
import Tabs, { TabContent, TabNav } from 'buyside/components/presentation/Tabs/tabsComponents';

import { IdeaPositionsDocument, IdeaPositionsQuery } from 'interfaces/graphql-typings';

const PositionInformation: FC<{ ideaId: string; loading: boolean }> = ({ ideaId, loading }) => {
  const client = useApolloClient();

  const cachedPosIdea = client.readQuery<IdeaPositionsQuery>({
    query: IdeaPositionsDocument,
    variables: { ideaId }
  })?.idea;

  const { positions } = cachedPosIdea || {};

  if (loading || !positions) return <PositionInformationLoading />;

  const equityPositions = positions.filter((pos): boolean => !pos.asset?.isCredit);
  const creditPositions = positions.filter((pos): boolean => Boolean(pos.asset?.isCredit));

  const isPairTrade = positions.length > 1;

  return (
    <InfoTable title="Entity Information">
      {({ dataStyle, labelStyle }): ReactElement => (
        <>
          {Boolean(equityPositions.length) && (
            <Tabs>
              <TabNav>
                <div>Fundamentals</div>
                <div>Basic Information</div>
                <div>Multiples/Ratios</div>
              </TabNav>
              {equityPositions.map(
                (position, idx): ReactElement => {
                  const isUserData =
                    !position.asset?.fundamental && Boolean(position.userCompanyData);
                  const optionalAsterisk = isUserData ? '*' : '';

                  return (
                    <div key={idx}>
                      {(isPairTrade || equityPositions.length > 1) && (
                        <div
                          className={classNames(styles.entityName, {
                            [styles.secondEntity]: idx !== 0
                          })}
                        >
                          {position.asset?.entity?.name}
                        </div>
                      )}
                      <TabContent>
                        <Fundamentals
                          currencyName={
                            position.asset?.fundamentalCurrencyName ||
                            position.asset?.currencyName ||
                            position.userCompanyData?.currencyName ||
                            'N/A'
                          }
                          dataStyle={dataStyle}
                          labelStyle={labelStyle}
                          range={((): string => {
                            const fiftyTwoWeekRangeHigh = position.asset?.fiftyTwoWeekRangeHigh;
                            const fiftyTwoWeekRangeLow = position.asset?.fiftyTwoWeekRangeLow;

                            return fiftyTwoWeekRangeLow && fiftyTwoWeekRangeHigh
                              ? `${fiftyTwoWeekRangeLow} - ${fiftyTwoWeekRangeHigh} ${position.asset
                                  ?.fundamentalCurrencyCode ||
                                  position.asset?.currencyCode ||
                                  position.userCompanyData?.currencyCode ||
                                  ''}`
                              : 'N/A';
                          })()}
                          fundamental={position.asset?.fundamental || null}
                          optionalAsterisk={optionalAsterisk}
                        />
                        <BasicInformation
                          dataStyle={dataStyle}
                          labelStyle={labelStyle}
                          companyData={position.asset?.companyData || null}
                        />
                        <MultiplesRatios
                          dataStyle={dataStyle}
                          labelStyle={labelStyle}
                          fundamental={position.asset?.fundamental || null}
                          optionalAsterisk={optionalAsterisk}
                        />
                      </TabContent>
                    </div>
                  );
                }
              )}
            </Tabs>
          )}
          {Boolean(creditPositions.length) && (
            <Tabs>
              <TabNav>
                <div>Basic Information</div>
                <div>Indenture Data</div>
                <div>Security Data</div>
              </TabNav>
              {creditPositions.map(
                (position, idx): ReactElement => {
                  return (
                    <div key={idx}>
                      {(isPairTrade || creditPositions.length > 1) && (
                        <div
                          className={classNames(styles.entityName, {
                            [styles.secondEntity]: idx !== 0
                          })}
                        >
                          {position.asset?.entity?.name}
                        </div>
                      )}
                      <TabContent>
                        <BasicInformation
                          dataStyle={dataStyle}
                          labelStyle={labelStyle}
                          companyData={position.asset?.companyData || null}
                        />
                        <IndentureData
                          currencyCode={position.asset?.currencyCode || ''}
                          dataStyle={dataStyle}
                          labelStyle={labelStyle}
                          userCreditData={position.userCreditData || null}
                        />
                        <SecurityData
                          dataStyle={dataStyle}
                          labelStyle={labelStyle}
                          userCreditData={position.userCreditData || null}
                        />
                      </TabContent>
                    </div>
                  );
                }
              )}
            </Tabs>
          )}
        </>
      )}
    </InfoTable>
  );
};

export default PositionInformation;
